import { createContext, useContext, useEffect, useState, type MouseEvent, type ReactNode } from "react";

export interface PageBack {
  label: string;
  to: string;
  /**
   * Runs before the back link navigates. Call `event.preventDefault()` to stay
   * (e.g. to ask about unsaved changes first). Keep it stable: `back` is an
   * effect dependency.
   */
  onClick?: (event: MouseEvent<HTMLAnchorElement>) => void;
}

interface PageTitle {
  title: ReactNode | null;
  subtitle: ReactNode | null;
  back: PageBack | null;
}

const EMPTY: PageTitle = { title: null, subtitle: null, back: null };

/*
 * Two contexts, so that SETTING the title never re-renders the page that set it.
 *
 * - The setter context holds `setState` from useState, which React keeps the
 *   same for the provider's whole life. Pages only read this one, so a title
 *   change never re-renders them.
 * - The value context holds the state object itself. Its identity changes only
 *   when a page sets a new title, so only readers (TopHeader) re-render.
 *
 * Both values are already stable, so neither needs a useMemo.
 *
 * BLU-1374: one context used to carry `{ ...state, setPageTitle }`, a new object
 * every render. Setting the title re-rendered the page, the page's effect saw a
 * new context and set the title again, forever (an idle /products page
 * committed about 210 renders in 3 s).
 */
const PageTitleSetterContext = createContext<((next: PageTitle) => void) | null>(null);
const PageTitleValueContext = createContext<PageTitle>(EMPTY);

export function PageTitleProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<PageTitle>(EMPTY);
  return (
    <PageTitleSetterContext.Provider value={setState}>
      <PageTitleValueContext.Provider value={state}>{children}</PageTitleValueContext.Provider>
    </PageTitleSetterContext.Provider>
  );
}

/** Read the current page title (TopHeader). EMPTY outside a provider. */
export function usePageTitleValue(): PageTitle {
  return useContext(PageTitleValueContext);
}

/**
 * Push the page's title into the TopHeader: set on mount and whenever an
 * argument changes, reset to empty on unmount. A no-op outside a provider
 * (production-layout pages have none).
 *
 * Every argument goes to the header exactly as given, and is compared by
 * identity. An inline `back` object or a JSX `title` / `subtitle` is new on
 * every render of the page, so it re-sets the header once each time the page
 * renders (never in a loop: setting the title does not re-render the page).
 * Memoise `back` and JSX titles if the page re-renders often. Never rebuild
 * `back` from some of its fields: a field you did not copy (a guard's
 * `onClick`, say) is silently dropped.
 */
export function usePageTitle(
  title: ReactNode,
  subtitle: ReactNode = null,
  back: PageBack | null = null,
) {
  const setPageTitle = useContext(PageTitleSetterContext);
  useEffect(() => {
    if (!setPageTitle) return;
    setPageTitle({ title, subtitle, back });
    return () => setPageTitle(EMPTY);
  }, [setPageTitle, title, subtitle, back]);
}
